import { context, SpanKind, SpanStatusCode, trace } from '@opentelemetry/api';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import {
  CompositePropagator,
  W3CBaggagePropagator,
  W3CTraceContextPropagator,
} from '@opentelemetry/core';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  BatchSpanProcessor,
  ConsoleSpanExporter,
  type SpanExporter,
} from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import {
  ATTR_HTTP_REQUEST_METHOD,
  ATTR_NETWORK_PROTOCOL_NAME,
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
  ATTR_URL_FULL,
} from '@opentelemetry/semantic-conventions';

import { isBrowser } from '@shared-web/utils/runtime';

import { getSamplerConfig, type ClientTelemetryConfig } from '../config';
import { CustomOTLPTraceExporter } from './custom-otlp-exporter';
import {
  AdaptiveSampler,
  createSampler,
  UserContextSpanProcessor,
} from './span-processors';

let tracerProvider: WebTracerProvider | null = null;
let unloadHandler: (() => void) | null = null;

const IGNORE_PATTERNS = [
  /cloudfront\.net/,
  /\/bff\/v1\/metrics\/report-metrics/,
  /\/bugsnag\.com\//,
  /otel/,
];

/**
 * Creates the appropriate sampler based on configuration.
 *
 * - If `useAdaptiveSampling` is true: Uses AdaptiveSampler with consumer-provided rules
 * - Otherwise: Uses environment-based sampling (ErrorAwareSampler wrapper)
 */
function createTraceSampler(config: ClientTelemetryConfig) {
  if (config.useAdaptiveSampling) {
    const { arg: defaultRate } = getSamplerConfig(config.environment);
    return new AdaptiveSampler(defaultRate, config.samplingRules || []);
  }

  const { type: samplerType, arg: samplerArg } = getSamplerConfig(
    config.environment,
  );
  return createSampler(samplerType, samplerArg);
}

export function setupTracing(config: ClientTelemetryConfig): void {
  if (tracerProvider) {
    console.warn('[OpenTelemetry] Tracing already initialized');
    return;
  }

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: config.serviceName,
    [ATTR_SERVICE_VERSION]: config.serviceVersion,
    'deployment.environment': config.environment,
    'device.type': config.deviceType,
    'user_agent.original':
      typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
  });

  const exporter = createExporter(config);
  const batchProcessor = new BatchSpanProcessor(exporter, {
    maxQueueSize: 2048,
    maxExportBatchSize: 100,
    scheduledDelayMillis: 5000,
    exportTimeoutMillis: 10000,
  });

  const userContextProcessor = new UserContextSpanProcessor();
  const sampler = createTraceSampler(config);

  tracerProvider = new WebTracerProvider({
    resource,
    sampler,
    spanProcessors: [userContextProcessor, batchProcessor],
  });

  tracerProvider.register({
    contextManager: new ZoneContextManager(),
    propagator: new CompositePropagator({
      propagators: [
        new W3CTraceContextPropagator(),
        new W3CBaggagePropagator(),
      ],
    }),
  });

  if (isBrowser()) {
    unloadHandler = () => {
      tracerProvider?.forceFlush();
    };
    window.addEventListener('pagehide', unloadHandler);
  }

  const isDocumentAlreadyLoaded =
    isBrowser() && document.readyState === 'complete';

  registerInstrumentations({
    tracerProvider,
    instrumentations: [
      getWebAutoInstrumentations({
        '@opentelemetry/instrumentation-xml-http-request': {
          propagateTraceHeaderCorsUrls: [/.+/g],
        },
        '@opentelemetry/instrumentation-fetch': {
          propagateTraceHeaderCorsUrls: [/.*/],
          clearTimingResources: false,
          applyCustomAttributesOnSpan(span, _request, response) {
            span.setAttribute(
              'app.synthetic_request',
              config.isSyntheticRequest || false,
            );
            if (response && 'headers' in response && response.headers) {
              const contentLength = response.headers.get('content-length');
              if (contentLength) {
                span.setAttribute(
                  'http.response.body.size',
                  parseInt(contentLength, 10),
                );
              }
              const contentType = response.headers.get('content-type');
              if (contentType) {
                span.setAttribute('http.response.content_type', contentType);
              }
            }
          },
          ignoreUrls: IGNORE_PATTERNS,
        },
        '@opentelemetry/instrumentation-document-load': {
          enabled: !isDocumentAlreadyLoaded,
        },
        '@opentelemetry/instrumentation-user-interaction': {
          enabled: false,
        },
      }),
    ],
  });

  if (isDocumentAlreadyLoaded) {
    scheduleManualDocumentLoadSpans();
  }

  if (config.debug) {
    console.log('[OpenTelemetry] Tracing initialized');
  }
}

const DOCUMENT_LOAD_SPAN_DELAY_MS = 3000;

function scheduleManualDocumentLoadSpans(): void {
  setTimeout(createManualDocumentLoadSpans, DOCUMENT_LOAD_SPAN_DELAY_MS);
}

function createManualDocumentLoadSpans(): void {
  const tracer = trace.getTracer(
    '@opentelemetry/instrumentation-document-load',
  );

  const navEntry = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  const resources = performance.getEntriesByType(
    'resource',
  ) as PerformanceResourceTiming[];

  const documentLoadStart = navEntry?.fetchStart || 0;
  const documentLoadEnd = navEntry?.loadEventEnd || performance.now();

  const rootSpan = tracer.startSpan('documentLoad', {
    kind: SpanKind.CLIENT,
    startTime: documentLoadStart,
    attributes: {
      [ATTR_URL_FULL]: window.location.href,
      'browser.document.ready_state': document.readyState,
      'http.request.header.referer': document.referrer || '',
    },
  });

  if (navEntry) {
    rootSpan.setAttribute('browser.navigation.type', navEntry.type);
    rootSpan.setAttribute(
      'browser.navigation.redirect_count',
      navEntry.redirectCount,
    );
    rootSpan.setAttribute(
      'browser.timing.dom_content_loaded_event',
      navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart,
    );
    rootSpan.setAttribute(
      'browser.timing.load_event',
      navEntry.loadEventEnd - navEntry.loadEventStart,
    );
    rootSpan.setAttribute('browser.timing.dom_complete', navEntry.domComplete);
    rootSpan.setAttribute(
      'browser.timing.dom_interactive',
      navEntry.domInteractive,
    );
  }

  const rootContext = trace.setSpan(context.active(), rootSpan);

  let resourceCount = 0;

  resources.forEach((resource) => {
    if (IGNORE_PATTERNS.some((pattern) => pattern.test(resource.name))) {
      return;
    }

    if (resource.fetchStart > documentLoadEnd) {
      return;
    }

    const resourceSpan = tracer.startSpan(
      `resourceFetch`,
      {
        kind: SpanKind.CLIENT,
        startTime: resource.fetchStart,
        attributes: {
          [ATTR_URL_FULL]: resource.name,
          [ATTR_HTTP_REQUEST_METHOD]: 'GET',
          [ATTR_NETWORK_PROTOCOL_NAME]: resource.nextHopProtocol || 'unknown',
          'http.response.body.size': resource.decodedBodySize,
          'http.response.transfer_size': resource.transferSize,
          'http.response.encoded_body_size': resource.encodedBodySize,
          'browser.resource.initiator_type': resource.initiatorType,
          'browser.resource.duration': resource.duration,
        },
      },
      rootContext,
    );

    resourceSpan.setStatus({ code: SpanStatusCode.OK });
    resourceSpan.end(resource.responseEnd);
    resourceCount++;
  });

  rootSpan.setAttribute('browser.resource.count', resourceCount);
  rootSpan.setStatus({ code: SpanStatusCode.OK });
  rootSpan.end(documentLoadEnd);
}

function createExporter(config: ClientTelemetryConfig): SpanExporter {
  if (config.debug) {
    return new ConsoleSpanExporter();
  }

  return new CustomOTLPTraceExporter({
    url: config.otlpEndpoint,
    timeoutMillis: 10000,
    concurrencyLimit: 50,
  });
}

export function isTracingInitialized(): boolean {
  return tracerProvider !== null;
}

export async function shutdownTracing(): Promise<void> {
  if (!tracerProvider) {
    return;
  }

  if (unloadHandler && isBrowser()) {
    window.removeEventListener('pagehide', unloadHandler);
    unloadHandler = null;
  }

  await tracerProvider.shutdown();
  tracerProvider = null;
}
